/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
  type ComboboxAction,
  type ComboboxFilterOption,
  type ComboboxOption,
  type ComboboxOptionGroup,
} from './combobox-types';

type ComboboxOptions<TOption extends ComboboxOption> =
  | TOption[]
  | ComboboxOptionGroup<TOption>[];

const isOptionGroup = <TOption extends ComboboxOption>(
  item: TOption | ComboboxOptionGroup<TOption>,
): item is ComboboxOptionGroup<TOption> => 'options' in item;

const matchesLabel = (option: ComboboxOption, searchValue: string) =>
  option.label.toLowerCase().includes(searchValue.toLowerCase());

/**
 * Filters options against the search value, keeping the grouped or flat shape
 * of the input. Groups that end up without matches are dropped.
 */
const filterComboboxOptions = <TOption extends ComboboxOption>(
  options: ComboboxOptions<TOption>,
  searchValue: string,
  filterOption?: ComboboxFilterOption<TOption>,
): ComboboxOptions<TOption> => {
  if (filterOption === false || searchValue.trim() === '') {
    return options;
  }

  const matches = filterOption ?? matchesLabel;

  if (options.every(isOptionGroup)) {
    return options
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => matches(option, searchValue)),
      }))
      .filter((group) => group.options.length > 0);
  }

  return (options as TOption[]).filter((option) =>
    matches(option, searchValue),
  );
};

/** Flattens grouped options into the visual order used for option ids. */
const flattenComboboxOptions = <TOption extends ComboboxOption>(
  options: (TOption | ComboboxOptionGroup<TOption>)[],
): TOption[] =>
  options.flatMap((item) => (isOptionGroup(item) ? item.options : item));

// A symbol keeps the marker out of consumer data and out of anything that walks
// option keys, so the create row travels through the list as a normal option.
const CREATE_MARKER = Symbol('ndlComboboxCreateOption');

type ComboboxCreateOption<TOption extends ComboboxOption> = TOption & {
  readonly [CREATE_MARKER]: true;
};

const isCreateOption = <TOption extends ComboboxOption>(
  option: TOption,
): option is ComboboxCreateOption<TOption> => CREATE_MARKER in option;

/** Tags an option as the create row, so activating it creates instead of selects. */
const asCreateOption = <TOption extends ComboboxOption>(
  option: TOption,
): ComboboxCreateOption<TOption> => ({
  ...option,
  [CREATE_MARKER]: true,
});

/**
 * The default way to build the option to create. It can only fill in the fields
 * of `ComboboxOption`, so an option type with more required fields of its own
 * has to supply `createOption`.
 */
const buildCreateOption = <TOption extends ComboboxOption>(
  searchValue: string,
): TOption =>
  ({
    label: searchValue,
    value: searchValue,
  }) as TOption;

const formatCreateOptionLabel = (searchValue: string) =>
  `Create "${searchValue}"`;

// Actions travel through the list as rows, which is what gives them virtual
// focus, an option id and keyboard activation. They cannot be options, since an
// option type with fields of its own is the consumer's to build, so they are
// their own row shape marked the same way as the create row.
const ACTION_MARKER = Symbol('ndlComboboxAction');

type ComboboxActionRow = ComboboxOption & {
  readonly [ACTION_MARKER]: ComboboxAction;
};

/** A row of the rendered list, which is either an option or an action. */
type ComboboxRow<TOption extends ComboboxOption> = TOption | ComboboxActionRow;

const isActionRow = <TOption extends ComboboxOption>(
  row: ComboboxRow<TOption>,
): row is ComboboxActionRow => ACTION_MARKER in row;

/**
 * Wraps an action as a list row. The position in `actions` gives the row a
 * stable value, which keeps the active row from being reset every render.
 */
const asActionRow = (
  action: ComboboxAction,
  index: number,
): ComboboxActionRow => ({
  isDisabled: action.isDisabled,
  label: action.label,
  value: `ndl-combobox-action-${index}`,
  [ACTION_MARKER]: action,
});

const getAction = (row: ComboboxActionRow) => row[ACTION_MARKER];

const matchesExactly = (option: ComboboxOption, searchValue: string) => {
  const normalized = searchValue.trim().toLowerCase();

  return (
    option.label.toLowerCase() === normalized ||
    option.value.toLowerCase() === normalized
  );
};

/**
 * A created option is never added to `options`, so the current selection is
 * checked too. Without that, retyping a created value would offer to create it
 * again and toggle the existing selection back off in multi select.
 */
const isValidNewComboboxOption = (
  searchValue: string,
  options: ComboboxOption[],
) =>
  searchValue.trim() !== '' &&
  !options.some((option) => matchesExactly(option, searchValue));

/**
 * Disabled options are deliberately included in keyboard navigation, following
 * the APG guidance on the focusability of disabled controls: skipping them
 * would make them undiscoverable to screen reader users. Activation is what
 * ignores them instead.
 */
const getFirstOptionIndex = (options: ComboboxOption[]) =>
  options.length > 0 ? 0 : -1;

const getLastOptionIndex = (options: ComboboxOption[]) => options.length - 1;

/**
 * Steps one option in the given direction, clamping at the ends of the list
 * as the combobox pattern permits.
 */
const getNextOptionIndex = (
  options: ComboboxOption[],
  fromIndex: number,
  direction: 1 | -1,
) => Math.min(Math.max(fromIndex + direction, 0), options.length - 1);

type ComboboxValue<TOption extends ComboboxOption> = TOption | TOption[] | null;

const getInitialValue = <TOption extends ComboboxOption>(
  value: ComboboxValue<TOption> | undefined,
  defaultValue: ComboboxValue<TOption> | undefined,
  isMulti: boolean,
): ComboboxValue<TOption> => {
  if (value !== undefined) {
    return value;
  }

  if (defaultValue !== undefined) {
    return defaultValue;
  }

  if (isMulti) {
    return [];
  }

  return null;
};

export {
  asActionRow,
  asCreateOption,
  buildCreateOption,
  filterComboboxOptions,
  flattenComboboxOptions,
  formatCreateOptionLabel,
  getAction,
  getFirstOptionIndex,
  getLastOptionIndex,
  getNextOptionIndex,
  isActionRow,
  isCreateOption,
  isOptionGroup,
  isValidNewComboboxOption,
  getInitialValue,
};
export type { ComboboxActionRow, ComboboxRow, ComboboxValue };
